import { motion, AnimatePresence } from 'framer-motion';
import { Rocket, Sparkles, X } from 'lucide-react';

const PARTICLES = Array.from({ length: 14 });

/**
 * UnlockModal, cinematic "New Level Unlocked: Launch Mode" reveal with badge
 * burst. Uses Framer Motion + AnimatePresence.
 */
export default function UnlockModal({ open, onClose, percent = 0, level = 'Launch Ready' }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          data-testid="launch-unlock-modal"
        >
          <motion.div className="absolute inset-0 bg-[#0c1222]/90 backdrop-blur-md" onClick={onClose} />
          <motion.div
            initial={{ scale: 0.85, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 200, damping: 20 }}
            className="relative w-full max-w-md rounded-3xl border border-cyan-400/40 bg-gradient-to-br from-slate-900 via-slate-900 to-blue-950/60 p-8 text-center drop-shadow-[0_0_40px_rgba(34,211,238,0.3)]"
          >
            <button onClick={onClose} className="absolute top-4 right-4 text-slate-300 hover:text-white" data-testid="unlock-modal-close">
              <X className="w-5 h-5" />
            </button>

            {/* Badge + particle burst */}
            <div className="relative mx-auto w-28 h-28 flex items-center justify-center">
              {PARTICLES.map((_, i) => {
                const angle = (i / PARTICLES.length) * Math.PI * 2;
                return (
                  <motion.span
                    key={i}
                    className="absolute w-1.5 h-1.5 rounded-full bg-cyan-300"
                    initial={{ x: 0, y: 0, opacity: 1, scale: 1 }}
                    animate={{ x: Math.cos(angle) * 80, y: Math.sin(angle) * 80, opacity: 0, scale: 0.4 }}
                    transition={{ duration: 1, delay: 0.15, ease: 'easeOut' }}
                  />
                );
              })}
              <motion.div
                initial={{ scale: 0, rotate: -30 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: 'spring', stiffness: 180, damping: 12, delay: 0.1 }}
                className="w-24 h-24 rounded-3xl bg-gradient-to-br from-cyan-400/30 to-blue-600/30 border border-cyan-400/60 flex items-center justify-center drop-shadow-[0_0_24px_rgba(34,211,238,0.6)]"
              >
                <Rocket className="w-11 h-11 text-cyan-200" />
              </motion.div>
            </div>

            <p className="mt-6 text-[11px] uppercase tracking-[0.25em] text-cyan-300">New Level Unlocked</p>
            <h2 className="mt-1 text-3xl font-outfit font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 to-blue-300">Launch Mode</h2>
            <p className="mt-3 text-slate-300 text-sm leading-relaxed">
              You've completed enough of your FuturePath Journey to generate your personalized Launch Pack. This is where clarity becomes action.
            </p>

            <div className="mt-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-400/30">
              <Sparkles className="w-4 h-4 text-cyan-300" />
              <span className="text-sm text-cyan-200 font-semibold">Launch Ready Badge earned · {percent}%</span>
            </div>

            <button
              onClick={onClose}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold hover:opacity-90 active:scale-95 transition"
              data-testid="unlock-modal-activate"
            >
              <Rocket className="w-4 h-4" /> Activate Launch Mode
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
